import { useCallback, useState } from "react";

import type { DocumentGroupFieldUpdate } from "services/document-group-api";

/** In-memory field edits keyed by document group ID, pending Apply. */
export type PendingDocumentGroupChange = DocumentGroupFieldUpdate;

export type PendingDocumentGroupChanges = Map<
  number,
  PendingDocumentGroupChange
>;

type UseLazyDocumentGroupUpdatesReturn = {
  pendingChanges: PendingDocumentGroupChanges;
  pendingChangeCount: number;
  hasPendingChanges: boolean;
  setPendingDocumentType: (groupId: number, documentType: string) => void;
  setPendingDate: (groupId: number, date: string | null) => void;
  setPendingProvider: (groupId: number, value: string | null) => void;
  setPendingFacility: (groupId: number, value: string | null) => void;
  setPendingDelete: (groupId: number) => void;
  removePendingDocumentType: (groupId: number) => void;
  removePendingDate: (groupId: number) => void;
  removePendingProvider: (groupId: number) => void;
  removePendingFacility: (groupId: number) => void;
  removePendingDelete: (groupId: number) => void;
  removePendingChange: (groupId: number) => void;
  discardPendingChanges: () => void;
};

// Returns a copy of `existing` without the named key. Uses explicit field copies
// rather than a spread + delete so that TypeScript narrows the result type
// correctly (spreading produces a wider object that includes the omitted key as
// optional, which confuses presence-sensitive checks like "date" in pending).
const withoutKey = (
  existing: PendingDocumentGroupChange,
  omit: keyof PendingDocumentGroupChange,
): PendingDocumentGroupChange => {
  const result: PendingDocumentGroupChange = {};
  if (omit !== "document_type" && existing.document_type !== undefined)
    result.document_type = existing.document_type;
  if (omit !== "date" && "date" in existing) result.date = existing.date;
  if (omit !== "provider" && "provider" in existing)
    result.provider = existing.provider;
  if (omit !== "facility" && "facility" in existing)
    result.facility = existing.facility;
  if (omit !== "delete" && existing.delete) result.delete = existing.delete;
  return result;
};

export const useLazyDocumentGroupUpdates =
  (): UseLazyDocumentGroupUpdatesReturn => {
    const [pendingChanges, setPendingChanges] =
      useState<PendingDocumentGroupChanges>(new Map());

    const setPendingDocumentType = useCallback(
      (groupId: number, documentType: string) => {
        setPendingChanges((current) => {
          const next = new Map(current);
          const existing = next.get(groupId) ?? {};
          next.set(groupId, { ...existing, document_type: documentType });
          return next;
        });
      },
      [],
    );

    const setPendingDate = useCallback(
      (groupId: number, date: string | null) => {
        setPendingChanges((current) => {
          const next = new Map(current);
          const existing = next.get(groupId) ?? {};
          next.set(groupId, { ...existing, date });
          return next;
        });
      },
      [],
    );

    const setPendingProvider = useCallback(
      (groupId: number, value: string | null) => {
        setPendingChanges((current) => {
          const next = new Map(current);
          const existing = next.get(groupId) ?? {};
          next.set(groupId, { ...existing, provider: value });
          return next;
        });
      },
      [],
    );

    const setPendingFacility = useCallback(
      (groupId: number, value: string | null) => {
        setPendingChanges((current) => {
          const next = new Map(current);
          const existing = next.get(groupId) ?? {};
          next.set(groupId, { ...existing, facility: value });
          return next;
        });
      },
      [],
    );

    const setPendingDelete = useCallback((groupId: number) => {
      setPendingChanges((current) => {
        const next = new Map(current);
        const existing = next.get(groupId) ?? {};
        next.set(groupId, { ...existing, delete: true });
        return next;
      });
    }, []);

    const removePendingDocumentType = useCallback((groupId: number) => {
      setPendingChanges((current) => {
        const existing = current.get(groupId);
        if (!existing) return current;
        const next = new Map(current);
        const rest = withoutKey(existing, "document_type");
        // Remove the map entry entirely when no pending fields remain so that
        // hasPendingChanges and pendingChangeCount stay accurate.
        if (Object.keys(rest).length === 0) {
          next.delete(groupId);
        } else {
          next.set(groupId, rest);
        }
        return next;
      });
    }, []);

    const removePendingDate = useCallback((groupId: number) => {
      setPendingChanges((current) => {
        const existing = current.get(groupId);
        if (!existing) return current;
        const next = new Map(current);
        const rest = withoutKey(existing, "date");
        if (Object.keys(rest).length === 0) {
          next.delete(groupId);
        } else {
          next.set(groupId, rest);
        }
        return next;
      });
    }, []);

    const removePendingProvider = useCallback((groupId: number) => {
      setPendingChanges((current) => {
        const existing = current.get(groupId);
        if (!existing) return current;
        const next = new Map(current);
        const rest = withoutKey(existing, "provider");
        if (Object.keys(rest).length === 0) {
          next.delete(groupId);
        } else {
          next.set(groupId, rest);
        }
        return next;
      });
    }, []);

    const removePendingFacility = useCallback((groupId: number) => {
      setPendingChanges((current) => {
        const existing = current.get(groupId);
        if (!existing) return current;
        const next = new Map(current);
        const rest = withoutKey(existing, "facility");
        if (Object.keys(rest).length === 0) {
          next.delete(groupId);
        } else {
          next.set(groupId, rest);
        }
        return next;
      });
    }, []);

    const removePendingDelete = useCallback((groupId: number) => {
      setPendingChanges((current) => {
        const existing = current.get(groupId);
        if (!existing) return current;
        const next = new Map(current);
        const rest = withoutKey(existing, "delete");
        if (Object.keys(rest).length === 0) {
          next.delete(groupId);
        } else {
          next.set(groupId, rest);
        }
        return next;
      });
    }, []);

    const removePendingChange = useCallback((groupId: number) => {
      setPendingChanges((current) => {
        if (!current.has(groupId)) return current;
        const next = new Map(current);
        next.delete(groupId);
        return next;
      });
    }, []);

    const discardPendingChanges = useCallback(() => {
      setPendingChanges(new Map());
    }, []);

    return {
      pendingChanges,
      pendingChangeCount: pendingChanges.size,
      hasPendingChanges: pendingChanges.size > 0,
      setPendingDocumentType,
      setPendingDate,
      setPendingProvider,
      setPendingFacility,
      setPendingDelete,
      removePendingDocumentType,
      removePendingDate,
      removePendingProvider,
      removePendingFacility,
      removePendingDelete,
      removePendingChange,
      discardPendingChanges,
    };
  };
